import React, { useState, useRef } from 'react';
import { useCompany } from '../../context/CompanyContext';
import { 
  Upload, Image as ImageIcon, CheckCircle, RefreshCw, 
  Trash2, Eye, Shield, Info, Building2, Sparkles, AlertCircle 
} from 'lucide-react';

export default function CompanyBrandingSettings() {
  const { branding, activeLogo, hasCustomLogo, companyName, companySubtitle, updateBranding, resetBranding, refreshBranding } = useCompany();

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(activeLogo || '');
  const [fileDetails, setFileDetails] = useState(null);
  const [formName, setFormName] = useState(companyName || 'TOYOTA MATERIAL HANDLING WAREHOUSE SOLUTIONS THAILAND');
  const [formSubtitle, setFormSubtitle] = useState(companySubtitle || 'Warehouse Solutions Thailand');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  const handleFileChange = (file) => {
    if (!file) return;

    // Validation: Image types
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setSaveError('กรุณาเลือกไฟล์ภาพที่ถูกต้อง (PNG, JPG, WEBP, หรือ SVG)');
      return;
    }

    // Size limit: 5MB
    if (file.size > 5 * 1024 * 1024) {
      setSaveError('ขนาดไฟล์ภาพต้องไม่เกิน 5 MB');
      return;
    }

    setSaveError(null);
    setSelectedFile(file);

    // Read details
    const sizeKb = (file.size / 1024).toFixed(1);
    setFileDetails({
      name: file.name,
      size: `${sizeKb} KB`,
      type: file.type
    });

    // Preview data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
      
      // Calculate dimensions
      const img = new Image();
      img.onload = () => {
        setFileDetails(prev => ({
          ...prev,
          width: img.naturalWidth,
          height: img.naturalHeight,
          ratio: (img.naturalWidth / img.naturalHeight).toFixed(2)
        }));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      let result;
      if (selectedFile) {
        // Upload via FormData
        const formData = new FormData();
        formData.append('logo', selectedFile);
        formData.append('company_name', formName);
        formData.append('company_subtitle', formSubtitle);
        result = await updateBranding(formData);
      } else {
        // Update text or preserve existing logo
        result = await updateBranding({
          company_name: formName,
          company_subtitle: formSubtitle,
          logo_data: previewUrl || branding.logo_data || '',
          logo_url: branding.logo_url || ''
        });
      }

      if (result.success) {
        setSaveSuccess(true);
        setSelectedFile(null);
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        setSaveError(result.error || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
      }
    } catch (err) {
      setSaveError(err.message || 'บันทึกข้อมูลไม่สำเร็จ');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('คุณแน่ใจหรือไม่ว่าต้องการคืนค่าโลโก้เป็นค่าเริ่มต้นของระบบ?')) return;
    setIsSaving(true);
    try {
      const res = await resetBranding();
      if (res.success) {
        setPreviewUrl('');
        setSelectedFile(null);
        setFileDetails(null);
        setFormName('TOYOTA MATERIAL HANDLING WAREHOUSE SOLUTIONS THAILAND');
        setFormSubtitle('Warehouse Solutions Thailand');
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="company-branding-settings max-w-5xl mx-auto space-y-8 pb-12">
      {/* Header Banner */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 shadow-sm">
            <Building2 size={30} />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
              ตั้งค่าโลโก้ & แบรนด์องค์กร (Company Branding)
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              อัปโหลดไฟล์ภาพโลโก้ที่เป็นทางการของ Toyota Material Handling เพื่อนำไปแสดงผลอัตโนมัติในทุกส่วนของระบบ
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasCustomLogo && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle size={14} /> โลโก้พร้อมใช้งาน
            </span>
          )}
        </div>
      </div>

      {/* Alerts */}
      {saveSuccess && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-5 py-4 rounded-xl flex items-center gap-3 animate-fadeIn shadow-sm">
          <CheckCircle size={22} className="text-emerald-600 flex-shrink-0" />
          <div>
            <h4 className="font-bold text-sm">บันทึกโลโก้และข้อมูลองค์กรเรียบร้อยแล้ว!</h4>
            <p className="text-xs text-emerald-700 mt-0.5">ระบบได้บันทึกข้อมูลลงฐานข้อมูลและอัปเดตโลโก้ในแถบเมนู, หน้าล็อกอิน, และเอกสาร Proposal ทุกแห่งเรียบร้อยแล้ว</p>
          </div>
        </div>
      )}

      {saveError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-xl flex items-center gap-3 animate-fadeIn shadow-sm">
          <AlertCircle size={22} className="text-red-600 flex-shrink-0" />
          <div className="text-sm font-semibold">{saveError}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Upload Box & Form Controls (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white rounded-2xl p-6 sm:p-7 shadow-sm border border-gray-200 space-y-6">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <Upload size={18} className="text-red-600" />
              1. อัปโหลดไฟล์ภาพโลโก้ (Upload Corporate Logo)
            </h3>

            {/* Drag & Drop Area */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
                isDragging 
                  ? 'border-red-500 bg-red-50/60 scale-[1.01]' 
                  : 'border-gray-300 hover:border-red-400 hover:bg-gray-50/80 bg-gray-50/40'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={(e) => handleFileChange(e.target.files?.[0])}
                className="hidden"
              />

              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white shadow-sm border border-gray-200 flex items-center justify-center text-red-600 group-hover:scale-110 transition-transform">
                <ImageIcon size={32} />
              </div>

              <h4 className="text-base font-bold text-gray-800">
                ลากและวางไฟล์โลโก้ที่นี่ หรือ <span className="text-red-600 underline">คลิกเพื่อเลือกไฟล์</span>
              </h4>
              <p className="text-xs text-gray-500 mt-2">
                รองรับไฟล์ <strong>PNG (แนะนำแบบพื้นหลังโปร่งใส), JPG, WEBP, SVG</strong> ขนาดไม่เกิน 5 MB
              </p>
              <p className="text-[11px] text-gray-400 mt-1">
                สัดส่วนที่เหมาะสม: สี่เหลี่ยมผืนผ้าแนวนอน (อัตราส่วนประมาณ 3:1 ถึง 5:1)
              </p>
            </div>

            {/* File Info Tag if Selected */}
            {fileDetails && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center gap-2 truncate">
                  <span className="font-semibold text-gray-900 truncate">{fileDetails.name}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">{fileDetails.size}</span>
                  {fileDetails.width && (
                    <>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-500">{fileDetails.width} × {fileDetails.height} px</span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setPreviewUrl(activeLogo || '');
                    setFileDetails(null);
                  }}
                  className="text-red-500 hover:text-red-700 font-semibold text-xs ml-2 cursor-pointer"
                >
                  ยกเลิก
                </button>
              </div>
            )}

            {/* Text Inputs */}
            <div className="space-y-4 pt-2">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
                <Building2 size={18} className="text-red-600" />
                2. ข้อมูลบริษัทและคำอธิบาย (Company Details)
              </h3>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  ชื่อบริษัท / องค์กร (Company Name)
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="TOYOTA MATERIAL HANDLING WAREHOUSE SOLUTIONS THAILAND"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-medium text-gray-900 focus:bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  คำอธิบาย / สายงาน (Subtitle / Division)
                </label>
                <input
                  type="text"
                  value={formSubtitle}
                  onChange={(e) => setFormSubtitle(e.target.value)}
                  placeholder="Warehouse Solutions Thailand"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-medium text-gray-900 focus:bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={isSaving || !hasCustomLogo}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-gray-600 hover:text-red-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RefreshCw size={14} />
                คืนค่าโลโก้เป็นค่าเริ่มต้น
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-xl shadow-md hover:shadow-lg transition-all cursor-pointer disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    กำลังบันทึกลงฐานข้อมูล...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    บันทึกโลโก้และข้อมูล (Save Branding)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Live Dual Previews (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white rounded-2xl p-6 sm:p-7 shadow-sm border border-gray-200 space-y-6">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <Eye size={18} className="text-red-600" />
              ตัวอย่างการแสดงผลจริง (Live Previews)
            </h3>

            {/* Preview Card A: Light Background (Sidebar / Proposal) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-gray-600">
                <span>1. บนพื้นหลังสว่าง (Sidebar & เอกสาร PDF)</span>
                <span className="text-[11px] font-normal text-gray-400">White Background</span>
              </div>
              <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-inner min-h-[110px] flex items-center justify-center relative overflow-hidden">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview Light"
                    className="max-h-12 max-w-[240px] object-contain drop-shadow-sm"
                  />
                ) : (
                  <div className="text-center">
                    <h1 className="text-3xl font-black text-red-600 tracking-tighter leading-none">TOYOTA</h1>
                    <div className="h-0.5 bg-gray-400 w-full my-1 rounded"></div>
                    <p className="text-[9px] font-bold text-gray-800 tracking-wider">
                      TOYOTA MATERIAL HANDLING<br/>WAREHOUSE SOLUTIONS THAILAND
                    </p>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-gray-400">
                * ภาพนี้จะนำไปแสดงตรงมุมซ้ายบนของแถบเมนูหลัก และหัวกระดาษของเอกสารข้อเสนอโครงการ
              </p>
            </div>

            {/* Preview Card B: Login Screen (White Card on Dark Background) */}
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between text-xs font-bold text-gray-600">
                <span>2. หน้าต่าง Login (หน้าต่างสีขาวบนพื้นหลังมืด)</span>
                <span className="text-[11px] font-normal text-gray-400">Login Window</span>
              </div>
              <div className="p-6 bg-gradient-to-br from-[#4a1212] to-[#0a0a0a] border border-gray-800 rounded-xl shadow-inner min-h-[140px] flex items-center justify-center relative overflow-hidden">
                <div className="bg-white px-8 py-5 rounded-2xl shadow-xl border border-gray-100 flex flex-col items-center justify-center min-w-[260px]">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview Login"
                      className="max-h-12 max-w-[220px] object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <h1 className="text-2xl font-black text-red-600 tracking-tighter leading-none">TOYOTA</h1>
                      <div className="h-0.5 bg-gray-300 w-full my-1 rounded"></div>
                      <p className="text-[9px] font-bold text-gray-800 tracking-wider">
                        TOYOTA MATERIAL HANDLING<br/>WAREHOUSE SOLUTIONS THAILAND
                      </p>
                    </div>
                  )}
                  <div className="mt-3 text-center">
                    <span className="text-xs font-bold text-gray-900 block">Salesvigator</span>
                    <span className="text-[9px] text-gray-500 block">กรุณาเข้าสู่ระบบ...</span>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-gray-400">
                * หน้าต่าง Login ถูกปรับเป็นพื้นหลังสีขาว ทำให้ตัวอักษรและโลโก้สีดำคมชัดและอ่านง่ายที่สุด
              </p>
            </div>

            {/* Info Badge */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 space-y-1.5">
              <div className="font-bold flex items-center gap-1.5 text-amber-900">
                <Sparkles size={14} className="text-amber-600" /> ข้อมูลความปลอดภัยและการจัดเก็บ:
              </div>
              <p className="leading-relaxed">
                ภาพโลโก้จะถูกแปลงและบันทึกเป็น <strong>Base64 Data URI</strong> ลงในฐานข้อมูล PostgreSQL โดยตรง ทำให้ข้อมูลคงอยู่ถาวรแม้มีการอัปเกรดหรือ Rebuild Docker Container บนเซิร์ฟเวอร์
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
